import {
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Close,
  Delete,
  ContentCopy,
  Add,
  LibraryAdd,
  PostAdd,
  Save,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { Col, Row } from "antd";
import { cloneDeep, findIndex, get, map } from "lodash";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import debounce from 'lodash/debounce';
import { useCollectionActivity } from 'utils/methods/firebaseActivity';
import { useFirebase } from 'containers/FirebaseContext';
import { DeleteModal } from 'components/Dialog';

const ModalContent = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: 20px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1300;
`;
const ModalConfigArea = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  gap: 20px;
  padding: 20px 10px;
  
  &::-webkit-scrollbar {
    height: 10px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ModalHeader = styled.div`
  padding-bottom: 8px;
  background: white;
`;
const ModalFooter = styled.div`
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  background: white;
`;
const LinkArea = styled.div`
  min-width: 400px;
  max-width: 450px;
  flex-shrink: 0;
  padding: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #fafafa;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
  
  &:hover {
    border-color: #1976d2;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;
const UnitContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  padding-top: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const UnitsSubArea = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SubUnitCard = styled.div`
  padding: 16px;
  border: 2px solid #f3e5f5;
  border-radius: 8px;
  background: #fefefe;
  box-shadow: 0 1px 4px rgba(156, 39, 176, 0.08);
  transition: all 0.2s;
  
  &:hover {
    border-color: #ce93d8;
    box-shadow: 0 2px 8px rgba(156, 39, 176, 0.15);
  }
`;
const SubLessonCard = styled.div`
  padding: 12px;
  border: 2px solid #e1bee7;
  border-radius: 6px;
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(156, 39, 176, 0.06);
  transition: all 0.2s;
  margin-top: 12px;
  margin-left: 16px;
  
  &:hover {
    border-color: #ba68c8;
    box-shadow: 0 2px 6px rgba(156, 39, 176, 0.12);
  }
`;
const TitleText = styled.span`
    font-size: 18px;
    color: black;
    font-weight:600;
    // margin-left:5px;
    // margin-top:5px;
`;
const PageOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const PageRangeInfo = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const RangeLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;
const UnitHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: white;
  border-radius: 6px;
  user-select: none;
  border-bottom: 2px solid #e0e0e0;
`;
const UnitTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;
const UnitNumber = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #1976d2;
`;
const UnitActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const UnitEditSection = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  border: 2px solid #90caf9;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
`;

const SubTopicUnits = ({ onCloseDrawer, data, onUpdateUnits, draftId, setDraftId, pages = [] }) => {
  const [subTopics, setSubTopics] = useState([])
  const unitScrollRef = useRef();
  const { firestore } = useFirebase();
  const { createDraftActivity, deleteDraftActivity } = useCollectionActivity();
  const unitRefs = useRef([]);
  const subunitRefs = useRef({});
  const sublessonRefs = useRef({});
  const [error, setError] = useState([])
  const [hasError, setHasError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(null);
  const [subunitAnchorEl, setSubunitAnchorEl] = useState(null);
  const [selectedSubunit, setSelectedSubunit] = useState(null);
  const [sublessonAnchorEl, setSublessonAnchorEl] = useState(null);
  const [selectedSublesson, setSelectedSublesson] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [sectionDeleteId, setSectionDeleteId] = useState(null);

  const closeDeleteSectionModal = useCallback(() => {
    setSectionDeleteId(null);
  }, []);

  useEffect(() => {
    setIsReady(false);
    setSubTopics(data ? data : [])
    requestAnimationFrame(() => {
      setIsReady(true);
    });
  }, [data])

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onCloseDrawer();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [onCloseDrawer]);

  const pageMap = useMemo(() => {
    const byPageId = {};
    pages.forEach(p => {
      byPageId[p.pageId] = p;
    });
    return byPageId;
  }, [pages]);

  const getPageById = useCallback((pageId) => {
    if (!pageId) return null;
    return pageMap[parseInt(pageId)] || null;
  }, [pageMap]);

  const renderPageOption = useCallback((page) => {
    if (!page) return null;
    return (
      <PageOption>
        <span style={{ fontWeight: 500 }}>{page.NumberMasked || page.pageId}</span>
      </PageOption>
    );
  }, []);

  const getFilteredPages = useCallback((startPageId = null) => {
    if (!startPageId) return pages;
    return pages.filter(p => p.pageId >= parseInt(startPageId));
  }, [pages]);

  const getFilteredPagesByUnitRange = useCallback((unitStartPage = null, unitFinishPage = null, startPageId = null) => {
    if (!unitStartPage || !unitFinishPage || unitStartPage === "" || unitFinishPage === "") {
      return pages;
    }
    
    const unitStart = parseInt(unitStartPage);
    const unitEnd = parseInt(unitFinishPage);
    
    if (isNaN(unitStart) || isNaN(unitEnd)) {
      return pages;
    }
    
    let filtered = pages.filter(p => {
      const pageId = parseInt(p.pageId);
      return !isNaN(pageId) && pageId >= unitStart && pageId <= unitEnd;
    });
    
    if (startPageId && startPageId !== "") {
      const startId = parseInt(startPageId);
      if (!isNaN(startId)) {
        filtered = filtered.filter(p => {
          const pageId = parseInt(p.pageId);
          return !isNaN(pageId) && pageId >= startId;
        });
      }
    }
    
    return filtered;
  }, [pages]);

  const duplicateUnit = (sb, index) => {
    const _subTopics = cloneDeep(subTopics);
    const lastElement = _subTopics[_subTopics.length - 1];
    let newSubTopicId = 1;
    if (lastElement) {
      newSubTopicId = parseInt(lastElement.subTopicId) + 1;
    }
    
    const duplicated = {
      ...cloneDeep(sb),
      subTopicId: newSubTopicId.toString(),
      shortName: `${sb.shortName} (Copy)`
    };
    
    _subTopics.splice(index + 1, 0, duplicated);
    setSubTopics(_subTopics);
    
    setTimeout(() => {
      const el = unitRefs.current[index + 1];
      if (el && unitScrollRef.current) {
        const scrollLeft = el.offsetLeft - 20;
        unitScrollRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const generateShortName = (startPageId, endPageId) => {
    const startPage = getPageById(startPageId);
    const endPage = getPageById(endPageId);
    if (startPage && endPage) {
      const start = startPage.NumberMasked || startPage.pageId;
      const end = endPage.NumberMasked || endPage.pageId;
      return start === end ? `Unit ${start}` : `Unit ${start}-${end}`;
    }
    return "";
  };

  
  const saveDraftRef = useRef(
    debounce(async (draftId, draftData) => {
      if (!draftId) return;
      try {
        await createDraftActivity(draftId, draftData);
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    }, 2000),
  );
  // Function to prepare draft data
  const prepareDraftData = () => ({
    subTopics: subTopics,
    activity_type: 'book_unit',
  });
  
  // Save draft when user stops typing or updating
  useEffect(() => {
    if (draftId) {
      const draftData = prepareDraftData();
      saveDraftRef.current(draftId, draftData);
    }
  }, [ subTopics ]);

  const handleDeleteDraft = async () => {
    if (!draftId) return;
    try {
     await deleteDraftActivity(draftId);
      setDraftId(null);
    } catch (error) {
      console.error('error', error);
    }
  };

  const onAddUnist = (index) => {
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    const lastElement = subTopics[subTopics.length - 1]
    let subTopicId = 1;
    if (lastElement) {
      subTopicId = parseInt(lastElement.subTopicId) + 1
    }
    _subTopics.push({
      "shortName": "",
      "startPage": "",
      "finishPage": "",
      "subTopicId": subTopicId?.toString(),
      "description": ""
    })
    setSubTopics(_subTopics)
    setTimeout(() => {
      if (unitScrollRef.current) {
        unitScrollRef.current.scrollTo({
          left: unitScrollRef.current.scrollWidth,
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  const handleAddUnitAfter = (index) => {
    const _subTopics = subTopics ? cloneDeep(subTopics) : [];

    const lastElement = _subTopics[_subTopics.length - 1];
    let subTopicId = 1;
    if (lastElement) {
      subTopicId = parseInt(lastElement.subTopicId) + 1;
    }
  
    const newSubTopic = {
      shortName: "",
      startPage: "",
      finishPage: "",
      subTopicId: subTopicId?.toString(),
      description: ""
    };
  
    _subTopics.splice(index + 1, 0, newSubTopic);
    setSubTopics(_subTopics);
  
    setTimeout(() => {
      const el = unitRefs.current[index + 1];
      if (el && unitScrollRef.current) {
        const scrollLeft = el.offsetLeft - 20;
        unitScrollRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }, 100);
  }
  const onClickAdd = () => {
      onAddUnist();
  }
  const onRemoveUnits = useCallback((id) => {
    const idx = findIndex(subTopics, k => k.subTopicId == id);
    if (idx < 0) return;
    const next = subTopics.filter((_, i) => i !== idx);
    onUpdateUnits(next);
    setSubTopics(next);
  }, [subTopics, onUpdateUnits]);

  const openDeleteSectionModal = useCallback((subTopicId) => {
    setSectionDeleteId(subTopicId);
  }, []);

  const handleConfirmDeleteSection = useCallback(() => {
    if (sectionDeleteId == null) return;
    onRemoveUnits(sectionDeleteId);
    setSectionDeleteId(null);
  }, [sectionDeleteId, onRemoveUnits]);

  const onChangeSubTopics = (id, data) => {
    const _units = findIndex(subTopics, k => k.subTopicId == id)
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    if (_units >= 0) {
      _subTopics[_units] = data;
    }
    setSubTopics(_subTopics)
  }
  const onAddSubunits = (id) => {
    const _units = findIndex(subTopics, k => k.subTopicId == id)
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    if (_units >= 0) {
      if (_subTopics[_units] && Array.isArray(_subTopics[_units].subUnits)) {
        _subTopics[_units].subUnits.push({
          "label": "",
          "start": '',
          "finish": ''
        })
      } else {
        _subTopics[_units].subUnits = [{
          "label": "",
          "start": '',
          "finish": ''
        }]
      }
      setSubTopics(_subTopics)
    }
  }
  const onRemoveSubunits = (id, index) => {
    const _units = findIndex(subTopics, k => k.subTopicId == id)
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    if (_units >= 0) {
      if (_subTopics[_units] && Array.isArray(_subTopics[_units].subUnits)) {
        _subTopics[_units].subUnits.splice(index, 1)
        setSubTopics(_subTopics)
        onUpdateUnits(_subTopics)
      }
    }
  }
  const onAddSublessons = (id, subunitIndex) => {
    const _units = findIndex(subTopics, k => k.subTopicId == id)
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    if (_units >= 0) {
      if (_subTopics[_units] && Array.isArray(_subTopics[_units].subUnits) && _subTopics[_units].subUnits[subunitIndex]) {
        if (Array.isArray(_subTopics[_units].subUnits[subunitIndex].subLessons)) {
          _subTopics[_units].subUnits[subunitIndex].subLessons.push({
            "label": "",
            "start": '',
            "finish": ''
          })
        } else {
          _subTopics[_units].subUnits[subunitIndex].subLessons = [{
            "label": "",
            "start": '',
            "finish": ''
          }]
        }
        setSubTopics(_subTopics)
      }
    }
  }
  const onRemoveSublessons = (id, subunitIndex, sublessonIndex) => {
    const _units = findIndex(subTopics, k => k.subTopicId == id)
    const _subTopics = subTopics ? cloneDeep(subTopics) : []
    if (_units >= 0) {
      if (_subTopics[_units] && Array.isArray(_subTopics[_units].subUnits) && _subTopics[_units].subUnits[subunitIndex]) {
        if (Array.isArray(_subTopics[_units].subUnits[subunitIndex].subLessons)) {
          _subTopics[_units].subUnits[subunitIndex].subLessons.splice(sublessonIndex, 1)
          setSubTopics(_subTopics)
          onUpdateUnits(_subTopics)
        }
      }
    }
  }
  const onUpdate = () => {
    const _error = []
    const errorMessages = [];
    
    map(subTopics, (sb, i) => {
      const sectionErrors = {
        "shortName": !sb.shortName,
        "startPage": !sb.startPage,
        "finishPage": !sb.finishPage,
        subUnits: map(get(sb, "subUnits"), su => ({
          "label": !su.label,
          "start": !su.start,
          "finish": !su.finish,
          subLessons: map(get(su, "subLessons"), sl => ({
            "label": !sl.label,
            "start": !sl.start,
            "finish": !sl.finish
          }))
        }))
      };
      
      _error.push(sectionErrors);
      
      if (sectionErrors.shortName) errorMessages.push(`Section #${i + 1}: Unit Name is required`);
      if (sectionErrors.startPage) errorMessages.push(`Section #${i + 1}: Start Page is required`);
      if (sectionErrors.finishPage) errorMessages.push(`Section #${i + 1}: End Page is required`);
      
      map(get(sb, "subUnits"), (su, subIdx) => {
        if (!su.label) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}: Label is required`);
        if (!su.start) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}: Start Page is required`);
        if (!su.finish) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}: End Page is required`);
        map(get(su, "subLessons"), (sl, sublessonIdx) => {
          if (!sl.label) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}, SubLesson #${sublessonIdx + 1}: Label is required`);
          if (!sl.start) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}, SubLesson #${sublessonIdx + 1}: Start Page is required`);
          if (!sl.finish) errorMessages.push(`Section #${i + 1}, Lesson #${subIdx + 1}, SubLesson #${sublessonIdx + 1}: End Page is required`);
        });
      });
    });
    
    if (JSON.stringify(_error).includes('true')) {
      setHasError(errorMessages)
      
      const firstErrorIndex = _error.findIndex(err => 
        err.shortName || err.startPage || err.finishPage || 
        (err.subUnits && JSON.stringify(err.subUnits).includes('true'))
      );
      
      if (firstErrorIndex >= 0) {
        setTimeout(() => {
          const el = unitRefs.current[firstErrorIndex];
          if (el && unitScrollRef.current) {
            const scrollLeft = el.offsetLeft - 20;
            unitScrollRef.current.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    } else {
      setHasError(false)
      onUpdateUnits(subTopics)
      handleDeleteDraft();
      onCloseDrawer()
    }
    setError(_error)
  }
  const onChangeUpdateError = (index, key) => {
    let e = cloneDeep(error);
    if (e[index]) {
      e[index][key] = false
      setError(e)
      setHasError(false)
    }
  }

  const onChangeSububitUpdateError = (index, i, key) => {
    let e = cloneDeep(error);
    if (e[index] && error[index].subUnits) {
      e[index].subUnits[i][key] = false
      setError(e)
      setHasError(false)
    }
  }
  const onChangeSublessonUpdateError = (index, i, j, key) => {
    let e = cloneDeep(error);
    if (e[index] && error[index].subUnits && error[index].subUnits[i] && error[index].subUnits[i].subLessons) {
      e[index].subUnits[i].subLessons[j][key] = false
      setError(e)
      setHasError(false)
    }
  }
  const getSubUnitError = (index, i, key) => {
    return get(error[index] && error[index].subUnits ? error[index].subUnits[i] : {}, key)
  }
  const getSublessonError = (index, i, j, key) => {
    return get(error[index]?.subUnits?.[i]?.subLessons?.[j] || {}, key)
  }
  
  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedUnitIndex(index);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUnitIndex(null);
  };
  
  const handleAddSectionAfter = () => {
    if (selectedUnitIndex !== null) {
      handleAddUnitAfter(selectedUnitIndex);
    }
    handleMenuClose();
  };
  
  const handleAddSubunit = () => {
    if (selectedUnitIndex !== null) {
      const sb = subTopics[selectedUnitIndex];
      onAddSubunits(sb.subTopicId);
      
      setTimeout(() => {
        const unitCard = unitRefs.current[selectedUnitIndex];
        if (unitCard) {
          const contentArea = unitCard.querySelector('[class*="UnitContentArea"]');
          if (contentArea) {
            contentArea.scrollTo({
              top: contentArea.scrollHeight,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    }
    handleMenuClose();
  };
  
  const handleSubunitMenuOpen = (event, unitIndex, subunitIndex) => {
    setSubunitAnchorEl(event.currentTarget);
    setSelectedSubunit({ unitIndex, subunitIndex });
  };
  
  const handleSubunitMenuClose = () => {
    setSubunitAnchorEl(null);
    setSelectedSubunit(null);
  };
  
  const handleAddSubunitBefore = () => {
    if (selectedSubunit !== null) {
      const { unitIndex, subunitIndex } = selectedSubunit;
      const _subTopics = cloneDeep(subTopics);
      const sb = _subTopics[unitIndex];
      
      const newSubunit = {
        "label": "",
        "start": '',
        "finish": ''
      };
      
      sb.subUnits.splice(subunitIndex, 0, newSubunit);
      setSubTopics(_subTopics);
      
      setTimeout(() => {
        const subunitKey = `${unitIndex}-${subunitIndex}`;
        const subunitEl = subunitRefs.current[subunitKey];
        if (subunitEl) {
          subunitEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
    handleSubunitMenuClose();
  };
  
  const handleAddSubunitAfter = () => {
    if (selectedSubunit !== null) {
      const { unitIndex, subunitIndex } = selectedSubunit;
      const _subTopics = cloneDeep(subTopics);
      const sb = _subTopics[unitIndex];
      
      const newSubunit = {
        "label": "",
        "start": '',
        "finish": ''
      };
      
      sb.subUnits.splice(subunitIndex + 1, 0, newSubunit);
      setSubTopics(_subTopics);
      
      setTimeout(() => {
        const subunitKey = `${unitIndex}-${subunitIndex + 1}`;
        const subunitEl = subunitRefs.current[subunitKey];
        if (subunitEl) {
          subunitEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
    handleSubunitMenuClose();
  };
  
  const handleSublessonMenuOpen = (event, unitIndex, subunitIndex, sublessonIndex) => {
    setSublessonAnchorEl(event.currentTarget);
    setSelectedSublesson({ unitIndex, subunitIndex, sublessonIndex });
  };
  
  const handleSublessonMenuClose = () => {
    setSublessonAnchorEl(null);
    setSelectedSublesson(null);
  };
  
  const handleAddSublessonBefore = () => {
    if (selectedSublesson !== null) {
      const { unitIndex, subunitIndex, sublessonIndex } = selectedSublesson;
      const _subTopics = cloneDeep(subTopics);
      const sb = _subTopics[unitIndex];
      
      if (sb.subUnits[subunitIndex]) {
        const newSublesson = {
          "label": "",
          "start": '',
          "finish": ''
        };
        
        if (!Array.isArray(sb.subUnits[subunitIndex].subLessons)) {
          sb.subUnits[subunitIndex].subLessons = [];
        }
        
        sb.subUnits[subunitIndex].subLessons.splice(sublessonIndex, 0, newSublesson);
        setSubTopics(_subTopics);
        
        setTimeout(() => {
          const sublessonKey = `${unitIndex}-${subunitIndex}-${sublessonIndex}`;
          const sublessonEl = sublessonRefs.current[sublessonKey];
          if (sublessonEl) {
            sublessonEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    }
    handleSublessonMenuClose();
  };
  
  const handleAddSublessonAfter = () => {
    if (selectedSublesson !== null) {
      const { unitIndex, subunitIndex, sublessonIndex } = selectedSublesson;
      const _subTopics = cloneDeep(subTopics);
      const sb = _subTopics[unitIndex];
      
      if (sb.subUnits[subunitIndex]) {
        const newSublesson = {
          "label": "",
          "start": '',
          "finish": ''
        };
        
        if (!Array.isArray(sb.subUnits[subunitIndex].subLessons)) {
          sb.subUnits[subunitIndex].subLessons = [];
        }
        
        sb.subUnits[subunitIndex].subLessons.splice(sublessonIndex + 1, 0, newSublesson);
        setSubTopics(_subTopics);
        
        setTimeout(() => {
          const sublessonKey = `${unitIndex}-${subunitIndex}-${sublessonIndex + 1}`;
          const sublessonEl = sublessonRefs.current[sublessonKey];
          if (sublessonEl) {
            sublessonEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    }
    handleSublessonMenuClose();
  };
  
  const totalSubUnits = useMemo(() => {
    return subTopics.reduce((acc, st) => acc + (st.subUnits?.length || 0), 0);
  }, [subTopics]);
  
  const totalSubLessons = useMemo(() => {
    return subTopics.reduce((acc, st) => {
      const lessonsCount = st.subUnits?.reduce((lessonAcc, su) => {
        return lessonAcc + (su.subLessons?.length || 0);
      }, 0) || 0;
      return acc + lessonsCount;
    }, 0);
  }, [subTopics]);
  
  const renderPageRangeInfoMemo = useCallback((startPageId, endPageId) => {
    if (!startPageId || !endPageId) return null;
    const startPage = getPageById(startPageId);
    const endPage = getPageById(endPageId);
    const start = parseInt(startPageId);
    const end = parseInt(endPageId);
    const count = pages.filter(p => p.pageId >= start && p.pageId <= end).length;
    
    return (
      <PageRangeInfo>
        <RangeLabel>Selected Range:</RangeLabel>
        {startPage && (
          <Chip 
            label={`${startPage.NumberMasked || startPage.pageId}`} 
            size="small" 
            color="primary"
          />
        )}
        <span style={{ color: '#999' }}>to</span>
        {endPage && (
          <Chip 
            label={`${endPage.NumberMasked || endPage.pageId}`} 
            size="small" 
            color="primary"
          />
        )}
        <Chip 
          label={`${count} page${count !== 1 ? 's' : ''}`} 
          size="small" 
          variant="outlined"
        />
      </PageRangeInfo>
    );
  }, [pages, getPageById]);
  
  if (!isReady) {
    return (
      <ModalContent>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontSize: '18px',
          color: '#999'
        }}>
          Loading...
        </div>
      </ModalContent>
    );
  }

  
  return (<ModalContent>
    <ModalHeader>
      <Row style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <TitleText>Manage Sections</TitleText>
          
          {subTopics.length > 0 && (
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center',
              paddingLeft: '20px',
              borderLeft: '2px solid #e0e0e0'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Sections</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1976d2' }}>{subTopics.length}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Lessons</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#9c27b0' }}>{totalSubUnits}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Sub Lessons</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#ab47bc' }}>{totalSubLessons}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Pages</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#666' }}>{pages.length}</div>
              </div>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onClickAdd}
            startIcon={<Add />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 2.5,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              }
            }}
          >
            Add Section
          </Button>
          
          <Button
            variant="contained"
            color="success"
            onClick={onUpdate}
            disabled={subTopics.length === 0}
            startIcon={<Save />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 2.5,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)',
              }
            }}
          >
            Update
          </Button>
          
          <IconButton
            onClick={() => onCloseDrawer()}
            sx={{ 
              padding: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <Close />
          </IconButton>
        </div>
      </Row>
      
      {hasError && Array.isArray(hasError) && hasError.length > 0 && (
        <div style={{ 
          marginTop: '12px', 
          padding: '12px 16px', 
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          border: '2px solid #ef5350',
          maxHeight: '120px',
          overflowY: 'auto'
        }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#d32f2f', fontSize: '14px' }}>
            ⚠️ Please fix the following errors:
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#c62828' }}>
            {hasError.map((msg, idx) => (
              <li key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
    </ModalHeader>
    
    <ModalConfigArea ref={unitScrollRef}>
      {subTopics.length === 0 ? (
        <EmptyState>
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
            <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>No Sections Yet</div>
            <div style={{ fontSize: '14px' }}>Click "Add Section" to create your first section</div>
          </div>
        </EmptyState>
      ) : null}
      {map(subTopics, (sb, index) => {
        const unitLabel = sb.shortName || `Unit ${index + 1}`;
        const pageRange = sb.startPage && sb.finishPage 
          ? `${getPageById(sb.startPage)?.NumberMasked || sb.startPage} - ${getPageById(sb.finishPage)?.NumberMasked || sb.finishPage}` 
          : "Not set";
        
        const sectionHasError = error[index] && (
          error[index].shortName || 
          error[index].startPage || 
          error[index].finishPage ||
          (error[index].subUnits && JSON.stringify(error[index].subUnits).includes('true'))
        );
        
        return (
        <LinkArea 
          key={sb.subTopicId} 
          ref={(el) => unitRefs.current[index] = el}
          style={{
            borderColor: sectionHasError ? '#ef5350' : '#e0e0e0',
            borderWidth: sectionHasError ? '3px' : '2px'
          }}
        >
          <UnitHeader>
            <UnitTitle>
              <UnitNumber>
                Section #{index + 1}
                {sectionHasError && (
                  <Chip 
                    label="!" 
                    size="small" 
                    color="error"
                    sx={{ 
                      height: '20px', 
                      fontSize: '12px', 
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}
                  />
                )}
              </UnitNumber>
              <div>
                <div style={{ fontWeight: 500 }}>{unitLabel}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{pageRange}</div>
              </div>
            </UnitTitle>
            <UnitActions>
              <Tooltip title="Add Options">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, index);
                  }}
                  style={{ padding: 4 }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Duplicate Section">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    duplicateUnit(sb, index);
                  }}
                  style={{ padding: 4 }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Section">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteSectionModal(sb.subTopicId);
                  }}
                  style={{ padding: 4 }}
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </UnitActions>
          </UnitHeader>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedUnitIndex === index}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                minWidth: 220,
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                mt: 1
              }
            }}
          >
            <MenuItem 
              onClick={handleAddSectionAfter}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                gap: 1.5,
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                  '& .MuiSvgIcon-root': {
                    color: '#1976d2'
                  }
                }
              }}
            >
              <PostAdd sx={{ fontSize: 20, color: '#1976d2' }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: '14px' }}>Add Section After</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Insert new section below</div>
              </div>
            </MenuItem>
                    <MenuItem 
                      onClick={handleAddSubunit}
                      sx={{
                        py: 1.5,
                        px: 2,
                        display: 'flex',
                        gap: 1.5,
                        '&:hover': {
                          backgroundColor: '#f3e5f5',
                          '& .MuiSvgIcon-root': {
                            color: '#9c27b0'
                          }
                        }
                      }}
                    >
                      <LibraryAdd sx={{ fontSize: 20, color: '#9c27b0' }} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Add Lesson</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Add lesson to section</div>
                      </div>
                    </MenuItem>
          </Menu>
          
          <UnitContentArea>
          <UnitEditSection>
            <Row style={{ marginBottom: 12 }} gutter={[12, 12]}>
              <Col span={12}>
                <TextField
                  id="shortName"
                  name="shortName"
                  fullWidth
                  error={get(error[index], "shortName")}
                  label="Unit Name"
                  placeholder={sb.startPage && sb.finishPage ? generateShortName(sb.startPage, sb.finishPage) : "e.g., Unit I-III"}
                  InputLabelProps={{ shrink: true, required: true }}
                  type="text"
                  variant="outlined"
                  onChange={(e) => {
                    onChangeUpdateError(index, "shortName")
                    onChangeSubTopics(sb.subTopicId, {
                      ...sb,
                      shortName: e.target.value,
                    });
                  }}
                  value={get(sb, "shortName")}
                  InputProps={{
                    endAdornment: sb.startPage && sb.finishPage && !sb.shortName ? (
                      <Button
                        size="small"
                        onClick={() => {
                          onChangeSubTopics(sb.subTopicId, {
                            ...sb,
                            shortName: generateShortName(sb.startPage, sb.finishPage),
                          });
                        }}
                      >
                        Auto
                      </Button>
                    ) : null
                  }}
                />
              </Col>
              <Col span={12}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  variant="outlined"
                  onChange={(e) => {
                    onChangeSubTopics(sb.subTopicId, {
                      ...sb,
                      description: e.target.value,
                    });
                  }}
                  value={get(sb, "description")}
                />
            </Col>
          </Row>
            {renderPageRangeInfoMemo(sb.startPage, sb.finishPage)}
            <Row style={{ marginBottom: 0, alignItems: 'center' }} gutter={[12, 12]}>
              <Col span={12}>
              <Autocomplete
                key={`start-${sb.subTopicId}-${sb.startPage}`}
                fullWidth
                value={getPageById(sb.startPage) || null}
                onChange={(event, newValue) => {
                  onChangeUpdateError(index, "startPage")
                  const newStartPage = newValue?.pageId?.toString() || "";
                  const updatedUnit = {
                    ...sb,
                    startPage: newStartPage,
                  };
                  if (newStartPage && sb.finishPage) {
                    const startPageNum = parseInt(newStartPage);
                    const finishPageNum = parseInt(sb.finishPage);
                    if (!isNaN(startPageNum) && !isNaN(finishPageNum) && startPageNum > finishPageNum) {
                      updatedUnit.finishPage = "";
                    } else if (!sb.shortName) {
                      updatedUnit.shortName = generateShortName(newValue.pageId, sb.finishPage);
                    }
                  }
                  onChangeSubTopics(sb.subTopicId, updatedUnit);
                }}
                options={pages}
                getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.pageId}>
                    {renderPageOption(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From"
                    error={get(error[index], "startPage")}
                    required
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
              />
            </Col>
            <Col span={12}>
              <Autocomplete
                key={`end-${sb.subTopicId}-${sb.finishPage}`}
                fullWidth
                value={getPageById(sb.finishPage) || null}
                onChange={(event, newValue) => {
                  onChangeUpdateError(index, "finishPage")
                  const updatedUnit = {
                    ...sb,
                    finishPage: newValue?.pageId?.toString() || "",
                  };
                  if (!sb.shortName && newValue && sb.startPage) {
                    updatedUnit.shortName = generateShortName(sb.startPage, newValue.pageId);
                  }
                  onChangeSubTopics(sb.subTopicId, updatedUnit);
                }}
                options={getFilteredPages(sb.startPage)}
                getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.pageId}>
                    {renderPageOption(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    error={get(error[index], "finishPage")}
                    required
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
              />
            </Col>
          </Row>
          </UnitEditSection>
          {get(sb, "subUnits") && get(sb, "subUnits").length > 0 && (
            <div>
              <div style={{ 
                marginTop: '8px', 
                marginBottom: '16px', 
                paddingTop: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                background: 'linear-gradient(90deg, #f3e5f5 0%, transparent 100%)',
                borderLeft: '3px solid #9c27b0',
                borderRadius: '4px'
              }}>
                <span style={{ fontWeight: 600, color: '#9c27b0', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Lessons</span>
              </div>
              <UnitsSubArea>
              {map(get(sb, "subUnits"), (su, i) => {
                const subunitHasError = error[index]?.subUnits?.[i] && (
                  error[index].subUnits[i].label ||
                  error[index].subUnits[i].start ||
                  error[index].subUnits[i].finish ||
                  (error[index].subUnits[i].subLessons && JSON.stringify(error[index].subUnits[i].subLessons).includes('true'))
                );
                
                return (
                <SubUnitCard 
                  key={i}
                  ref={(el) => {
                    if (el) subunitRefs.current[`${index}-${i}`] = el;
                  }}
                  style={{
                    borderColor: subunitHasError ? '#ef5350' : '#f3e5f5',
                    borderWidth: subunitHasError ? '3px' : '2px'
                  }}
                >
                  <Row
                    gutter={[24, 24]}
                    style={{ justifyContent: "space-between", display: "flex", alignItems: "center", marginBottom: 12, paddingLeft: 8 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 500, color: '#1976d2' }}>Lessons #{i + 1}</span>
                      {subunitHasError && (
                        <Chip 
                          label="!" 
                          size="small" 
                          color="error"
                          sx={{ 
                            height: '18px', 
                            fontSize: '11px', 
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Tooltip title="Add Lesson Options">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubunitMenuOpen(e, index, i);
                          }}
                          style={{ padding: 4 }}
                          size="small"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Lesson">
                        <IconButton
                          onClick={() => onRemoveSubunits(sb.subTopicId, i)}
                          style={{ padding: 4 }}
                          size="small"
                        >
                          <Delete color="secondary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Row>
                  <Menu
                    anchorEl={subunitAnchorEl}
                    open={Boolean(subunitAnchorEl) && selectedSubunit?.unitIndex === index && selectedSubunit?.subunitIndex === i}
                    onClose={handleSubunitMenuClose}
                    onClick={(e) => e.stopPropagation()}
                    PaperProps={{
                      sx: {
                        minWidth: 220,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        mt: 1
                      }
                    }}
                  >
                    <MenuItem 
                      onClick={handleAddSubunitBefore}
                      sx={{
                        py: 1.5,
                        px: 2,
                        display: 'flex',
                        gap: 1.5,
                        '&:hover': {
                          backgroundColor: '#e8f5e9',
                          '& .MuiSvgIcon-root': {
                            color: '#2e7d32'
                          }
                        }
                      }}
                    >
                      <ArrowUpward sx={{ fontSize: 20, color: '#2e7d32' }} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Add Lesson Before</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Insert new lesson above</div>
                      </div>
                    </MenuItem>
                    <MenuItem 
                      onClick={handleAddSubunitAfter}
                      sx={{
                        py: 1.5,
                        px: 2,
                        display: 'flex',
                        gap: 1.5,
                        '&:hover': {
                          backgroundColor: '#fff3e0',
                          '& .MuiSvgIcon-root': {
                            color: '#f57c00'
                          }
                        }
                      }}
                    >
                      <ArrowDownward sx={{ fontSize: 20, color: '#f57c00' }} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Add Subunit After</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Insert new subunit below</div>
                      </div>
                    </MenuItem>
                  </Menu>
                  {renderPageRangeInfoMemo(su.start, su.finish)}
                  <Row style={{ marginBottom: 12 }}>
                <Col span={24}>
                  <TextField
                    id="label"
                    name="label"
                    label="Label"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={getSubUnitError(index, i, "label")}
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => {
                      onChangeSububitUpdateError(index, i, "label")
                      const v = cloneDeep(sb);
                      v.subUnits[i].label = e.target.value;
                      onChangeSubTopics(sb.subTopicId, v);
                    }}
                    value={get(su, "label")}
                  />
                </Col>
              </Row>
              <Row style={{ marginBottom: 12 }}>
                <Col span={24}>
                  <Autocomplete
                    key={`subunit-start-${sb.subTopicId}-${i}-${su.start}`}
                    fullWidth
                    value={getPageById(su.start) || null}
                    onChange={(event, newValue) => {
                      onChangeSububitUpdateError(index, i, "start")
                      const v = cloneDeep(sb);
                      const newStartPage = newValue?.pageId?.toString() || "";
                      v.subUnits[i].start = newStartPage;
                      if (newStartPage && v.subUnits[i].finish) {
                        const startPageNum = parseInt(newStartPage);
                        const finishPageNum = parseInt(v.subUnits[i].finish);
                        if (!isNaN(startPageNum) && !isNaN(finishPageNum) && startPageNum > finishPageNum) {
                          v.subUnits[i].finish = "";
                        }
                      }
                      onChangeSubTopics(sb.subTopicId, v);
                    }}
                    options={getFilteredPagesByUnitRange(sb.startPage, sb.finishPage)}
                    getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                    renderOption={(props, option) => (
                      <li {...props} key={option.pageId}>
                        {renderPageOption(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Start Page"
                        error={getSubUnitError(index, i, "start")}
                        required
                        variant="outlined"
                        size="small"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
                  />
                </Col>
              </Row>
              <Row style={{ marginBottom: 12 }}>
                <Col span={24}>
                  <Autocomplete
                    key={`subunit-end-${sb.subTopicId}-${i}-${su.finish}`}
                    fullWidth
                    value={getPageById(su.finish) || null}
                    onChange={(event, newValue) => {
                      onChangeSububitUpdateError(index, i, "finish")
                      const v = cloneDeep(sb);
                      v.subUnits[i].finish = newValue?.pageId?.toString() || "";
                      onChangeSubTopics(sb.subTopicId, v);
                    }}
                    options={getFilteredPagesByUnitRange(sb.startPage, sb.finishPage, su.start || sb.startPage)}
                    getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                    renderOption={(props, option) => (
                      <li {...props} key={option.pageId}>
                        {renderPageOption(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="End Page"
                        error={getSubUnitError(index, i, "finish")}
                        required
                        variant="outlined"
                        size="small"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
                  />
                </Col>
              </Row>
              {get(su, "subLessons") && get(su, "subLessons").length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    paddingTop: '8px',
                    paddingBottom: '6px',
                    paddingLeft: '10px',
                    background: 'linear-gradient(90deg, #e1bee7 0%, transparent 100%)',
                    borderLeft: '3px solid #ab47bc',
                    borderRadius: '4px'
                  }}>
                    <span style={{ fontWeight: 600, color: '#ab47bc', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SubLessons</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {map(get(su, "subLessons"), (sl, j) => {
                      const sublessonHasError = error[index]?.subUnits?.[i]?.subLessons?.[j] && (
                        error[index].subUnits[i].subLessons[j].label ||
                        error[index].subUnits[i].subLessons[j].start ||
                        error[index].subUnits[i].subLessons[j].finish
                      );
                      
                      return (
                        <SubLessonCard 
                          key={j}
                          ref={(el) => {
                            if (el) sublessonRefs.current[`${index}-${i}-${j}`] = el;
                          }}
                          style={{
                            borderColor: sublessonHasError ? '#ef5350' : '#e1bee7',
                            borderWidth: sublessonHasError ? '3px' : '2px'
                          }}
                        >
                          <Row
                            gutter={[24, 24]}
                            style={{ justifyContent: "space-between", display: "flex", alignItems: "center", marginBottom: 12, paddingLeft: 8 }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 500, color: '#ab47bc', fontSize: '13px' }}>SubLesson #{j + 1}</span>
                              {sublessonHasError && (
                                <Chip 
                                  label="!" 
                                  size="small" 
                                  color="error"
                                  sx={{ 
                                    height: '16px', 
                                    fontSize: '10px', 
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <Tooltip title="Add SubLesson Options">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSublessonMenuOpen(e, index, i, j);
                                  }}
                                  style={{ padding: 4 }}
                                  size="small"
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete SubLesson">
                                <IconButton
                                  onClick={() => onRemoveSublessons(sb.subTopicId, i, j)}
                                  style={{ padding: 4 }}
                                  size="small"
                                >
                                  <Delete color="secondary" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </Row>
                          <Menu
                            anchorEl={sublessonAnchorEl}
                            open={Boolean(sublessonAnchorEl) && selectedSublesson?.unitIndex === index && selectedSublesson?.subunitIndex === i && selectedSublesson?.sublessonIndex === j}
                            onClose={handleSublessonMenuClose}
                            onClick={(e) => e.stopPropagation()}
                            PaperProps={{
                              sx: {
                                minWidth: 220,
                                borderRadius: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                mt: 1
                              }
                            }}
                          >
                            <MenuItem 
                              onClick={handleAddSublessonBefore}
                              sx={{
                                py: 1.5,
                                px: 2,
                                display: 'flex',
                                gap: 1.5,
                                '&:hover': {
                                  backgroundColor: '#e8f5e9',
                                  '& .MuiSvgIcon-root': {
                                    color: '#2e7d32'
                                  }
                                }
                              }}
                            >
                              <ArrowUpward sx={{ fontSize: 20, color: '#2e7d32' }} />
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '14px' }}>Add SubLesson Before</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Insert new sublesson above</div>
                              </div>
                            </MenuItem>
                            <MenuItem 
                              onClick={handleAddSublessonAfter}
                              sx={{
                                py: 1.5,
                                px: 2,
                                display: 'flex',
                                gap: 1.5,
                                '&:hover': {
                                  backgroundColor: '#fff3e0',
                                  '& .MuiSvgIcon-root': {
                                    color: '#f57c00'
                                  }
                                }
                              }}
                            >
                              <ArrowDownward sx={{ fontSize: 20, color: '#f57c00' }} />
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '14px' }}>Add SubLesson After</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Insert new sublesson below</div>
                              </div>
                            </MenuItem>
                          </Menu>
                          {renderPageRangeInfoMemo(sl.start, sl.finish)}
                          <Row style={{ marginBottom: 12 }}>
                            <Col span={24}>
                              <TextField
                                id="label"
                                name="label"
                                label="Label"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                error={getSublessonError(index, i, j, "label")}
                                type="text"
                                variant="outlined"
                                size="small"
                                onChange={(e) => {
                                  onChangeSublessonUpdateError(index, i, j, "label")
                                  const v = cloneDeep(sb);
                                  if (!Array.isArray(v.subUnits[i].subLessons)) {
                                    v.subUnits[i].subLessons = [];
                                  }
                                  v.subUnits[i].subLessons[j].label = e.target.value;
                                  onChangeSubTopics(sb.subTopicId, v);
                                }}
                                value={get(sl, "label")}
                              />
                            </Col>
                          </Row>
                          <Row style={{ marginBottom: 12 }}>
                            <Col span={24}>
                              <Autocomplete
                                key={`sublesson-start-${sb.subTopicId}-${i}-${j}-${sl.start}`}
                                fullWidth
                                value={getPageById(sl.start) || null}
                                onChange={(event, newValue) => {
                                  onChangeSublessonUpdateError(index, i, j, "start")
                                  const v = cloneDeep(sb);
                                  if (!Array.isArray(v.subUnits[i].subLessons)) {
                                    v.subUnits[i].subLessons = [];
                                  }
                                  const newStartPage = newValue?.pageId?.toString() || "";
                                  v.subUnits[i].subLessons[j].start = newStartPage;
                                  if (newStartPage && v.subUnits[i].subLessons[j].finish) {
                                    const startPageNum = parseInt(newStartPage);
                                    const finishPageNum = parseInt(v.subUnits[i].subLessons[j].finish);
                                    if (!isNaN(startPageNum) && !isNaN(finishPageNum) && startPageNum > finishPageNum) {
                                      v.subUnits[i].subLessons[j].finish = "";
                                    }
                                  }
                                  onChangeSubTopics(sb.subTopicId, v);
                                }}
                                options={getFilteredPagesByUnitRange(sb.startPage, sb.finishPage)}
                                getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.pageId}>
                                    {renderPageOption(option)}
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Start Page"
                                    error={getSublessonError(index, i, j, "start")}
                                    required
                                    variant="outlined"
                                    size="small"
                                  />
                                )}
                                isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
                              />
                            </Col>
                          </Row>
                          <Row style={{ marginBottom: 12 }}>
                            <Col span={24}>
                              <Autocomplete
                                key={`sublesson-end-${sb.subTopicId}-${i}-${j}-${sl.finish}`}
                                fullWidth
                                value={getPageById(sl.finish) || null}
                                onChange={(event, newValue) => {
                                  onChangeSublessonUpdateError(index, i, j, "finish")
                                  const v = cloneDeep(sb);
                                  if (!Array.isArray(v.subUnits[i].subLessons)) {
                                    v.subUnits[i].subLessons = [];
                                  }
                                  v.subUnits[i].subLessons[j].finish = newValue?.pageId?.toString() || "";
                                  onChangeSubTopics(sb.subTopicId, v);
                                }}
                                options={getFilteredPagesByUnitRange(sb.startPage, sb.finishPage, sl.start || su.start || sb.startPage)}
                                getOptionLabel={(option) => option?.NumberMasked || option?.pageId?.toString() || ''}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.pageId}>
                                    {renderPageOption(option)}
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="End Page"
                                    error={getSublessonError(index, i, j, "finish")}
                                    required
                                    variant="outlined"
                                    size="small"
                                  />
                                )}
                                isOptionEqualToValue={(option, value) => option?.pageId === value?.pageId}
                              />
                            </Col>
                          </Row>
                        </SubLessonCard>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ marginTop: '12px', marginLeft: '8px' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onAddSublessons(sb.subTopicId, i)}
                  startIcon={<Add />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#ab47bc',
                    color: '#ab47bc',
                    '&:hover': {
                      borderColor: '#9c27b0',
                      backgroundColor: '#f3e5f5'
                    }
                  }}
                >
                  Add SubLesson
                </Button>
              </div>
                </SubUnitCard>
              )})}
            </UnitsSubArea>
            </div>
          )}
          </UnitContentArea>
        </LinkArea>
      )})}
    </ModalConfigArea>

    {sectionDeleteId != null ? (
      <DeleteModal
        message="Are you sure you want to delete this section?"
        deleteToggle
        setDeleteToggle={closeDeleteSectionModal}
        loading={false}
        onDelete={handleConfirmDeleteSection}
      />
    ) : null}

  </ModalContent>)
}

export default SubTopicUnits;